import { FormEvent, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, KeyRound, LoaderCircle, Save, ShieldCheck, Trash2, UserPlus, Users } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import {
  authApi,
  hasPermission,
  permissionLabels,
  usersApi,
  type AppPermission,
  type AppUser
} from "../../services/authApi";

const allPermissions = Object.keys(permissionLabels) as AppPermission[];
const emptyUserForm = {
  email: "",
  name: "",
  password: "",
  permissions: ["operator_automation"] as AppPermission[]
};
const emptyPasswordForm = { currentPassword: "", newPassword: "", confirmPassword: "" };

export function GlobalSettingsPage() {
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const canManageAccess = hasPermission(user, "admin");
  const [form, setForm] = useState(emptyUserForm);
  const [formError, setFormError] = useState("");
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [accessMessage, setAccessMessage] = useState("");

  const usersQuery = useQuery({ queryKey: ["users"], queryFn: usersApi.list, enabled: canManageAccess });
  const createUser = useMutation({
    mutationFn: usersApi.create,
    onMutate: () => setAccessMessage(""),
    onSuccess: () => {
      setForm(emptyUserForm);
      setFormError("");
      setAccessMessage("Käyttäjä lisättiin onnistuneesti.");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Käyttäjän lisäys epäonnistui.")
  });
  const updateUser = useMutation({
    mutationFn: ({ email, name, permissions }: { email: string; name: string; permissions: AppPermission[] }) => usersApi.updateAccess(email, { name, permissions }),
    onMutate: () => setAccessMessage(""),
    onSuccess: (updated) => {
      setAccessMessage(`Käyttäjän ${updated.email} käyttöoikeudet tallennettiin.`);
      queryClient.invalidateQueries({ queryKey: ["users"] });
      if (updated.email === user?.email) refreshUser();
    }
  });
  const deleteUser = useMutation({
    mutationFn: usersApi.delete,
    onMutate: () => setAccessMessage(""),
    onSuccess: () => {
      setAccessMessage("Käyttäjä poistettiin onnistuneesti.");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    }
  });
  const changePassword = useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      setPasswordForm(emptyPasswordForm);
      setPasswordError("");
      setPasswordMessage("Salasana vaihdettiin onnistuneesti.");
    },
    onError: (error) => {
      setPasswordMessage("");
      setPasswordError(error instanceof Error ? error.message : "Salasanan vaihto epäonnistui.");
    }
  });

  const submitUser = (event: FormEvent) => {
    event.preventDefault();
    setFormError("");
    setAccessMessage("");
    if (!form.email.trim() || !form.name.trim() || !form.password) return setFormError("Täytä sähköposti, nimi ja väliaikainen salasana.");
    if (form.password.length < 8) return setFormError("Väliaikaisen salasanan pitää olla vähintään 8 merkkiä.");
    if (!form.permissions.length) return setFormError("Valitse käyttäjälle vähintään yksi käyttöoikeus.");
    createUser.mutate(form);
  };

  const submitPassword = (event: FormEvent) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordMessage("");
    if (!passwordForm.currentPassword || !passwordForm.newPassword) return setPasswordError("Täytä nykyinen ja uusi salasana.");
    if (passwordForm.newPassword.length < 8) return setPasswordError("Uuden salasanan pitää olla vähintään 8 merkkiä.");
    if (passwordForm.newPassword !== passwordForm.confirmPassword) return setPasswordError("Uusi salasana ja vahvistus eivät täsmää.");
    changePassword.mutate({ currentPassword: passwordForm.currentPassword, newPassword: passwordForm.newPassword });
  };

  return <>
    <section className="page-heading">
      <div><span className="kicker">YLLÄPITO</span><h1>Yleiset asetukset</h1><p>Oma käyttäjätili ja Ällistämö Expressin käyttöoikeudet.</p></div>
    </section>

    <div className="global-settings-grid">
      <section className="card settings-card account-card">
        <div className="settings-card__title"><span><ShieldCheck /></span><div><h2>Oma käyttäjätili</h2><p>Kirjautuneen käyttäjän tiedot ja oikeudet</p></div></div>
        <div className="account-identity"><span>{user?.name?.slice(0, 1).toUpperCase()}</span><div><strong>{user?.name}</strong><small>{user?.email}</small></div></div>
        <div className="permission-summary">{allPermissions.filter((permission) => hasPermission(user, permission)).map((permission) => <span key={permission}><Check />{permissionLabels[permission]}</span>)}</div>
      </section>

      <section className="card settings-card">
        <div className="settings-card__title"><span><KeyRound /></span><div><h2>Vaihda salasana</h2><p>Päivitä oman käyttäjätunnuksesi salasana</p></div></div>
        <form className="password-form password-form--global" onSubmit={submitPassword}>
          <label className="field"><span>Nykyinen salasana</span><input type="password" autoComplete="current-password" value={passwordForm.currentPassword} onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })} /></label>
          <label className="field"><span>Uusi salasana</span><input type="password" autoComplete="new-password" value={passwordForm.newPassword} onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })} /></label>
          <label className="field"><span>Vahvista uusi salasana</span><input type="password" autoComplete="new-password" value={passwordForm.confirmPassword} onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })} /></label>
          <button className="button button--primary" disabled={changePassword.isPending}>{changePassword.isPending ? <LoaderCircle className="spin" /> : <KeyRound />}Vaihda salasana</button>
        </form>
        {passwordMessage && <div className="inline-success">{passwordMessage}</div>}
        {passwordError && <div className="inline-error">{passwordError}</div>}
      </section>
    </div>

    {canManageAccess && <section className="card settings-card access-manager">
      <div className="settings-card__title"><span><Users /></span><div><h2>Käyttäjät ja käyttöoikeudet</h2><p>Rajaa pääsy käyttöoikeushallintaan ja moduuleihin käyttäjäkohtaisesti.</p></div></div>
      <form className="access-create-form" onSubmit={submitUser}>
        <div className="access-create-form__identity">
          <label className="field"><span>Nimi</span><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
          <label className="field"><span>Sähköposti</span><input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
          <label className="field"><span>Väliaikainen salasana</span><input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} /></label>
        </div>
        <PermissionSelector value={form.permissions} onChange={(permissions) => setForm({ ...form, permissions })} />
        <button className="button button--primary" disabled={createUser.isPending}>{createUser.isPending ? <LoaderCircle className="spin" /> : <UserPlus />}Lisää käyttäjä</button>
      </form>
      {formError && <div className="inline-error">{formError}</div>}
      {accessMessage && <div className="inline-success">{accessMessage}</div>}
      {usersQuery.isError && <div className="inline-error">Käyttäjiä ei voitu ladata.</div>}
      {usersQuery.isLoading ? <div className="muted">Ladataan käyttäjiä…</div> : <div className="access-user-list">
        {(usersQuery.data ?? []).map((item) => <UserAccessRow
          key={item.email}
          item={item}
          currentUserEmail={user?.email ?? ""}
          saving={updateUser.isPending}
          deleting={deleteUser.isPending}
          onSave={(payload) => updateUser.mutate(payload)}
          onDelete={(email) => { if (window.confirm(`Poistetaanko käyttäjä ${email}?`)) deleteUser.mutate(email); }}
        />)}
      </div>}
      {updateUser.isError && <div className="inline-error">{updateUser.error instanceof Error ? updateUser.error.message : "Käyttöoikeuksien tallennus epäonnistui."}</div>}
      {deleteUser.isError && <div className="inline-error">{deleteUser.error instanceof Error ? deleteUser.error.message : "Käyttäjän poistaminen epäonnistui."}</div>}
    </section>}
  </>;
}

function PermissionSelector({ value, onChange, lockAdmin = false }: { value: AppPermission[]; onChange: (value: AppPermission[]) => void; lockAdmin?: boolean }) {
  const toggle = (permission: AppPermission) => {
    if (lockAdmin && permission === "admin") return;
    onChange(value.includes(permission) ? value.filter((item) => item !== permission) : [...value, permission]);
  };
  return <fieldset className="permission-selector"><legend>Käyttöoikeudet</legend>{allPermissions.map((permission) => <label key={permission} className={value.includes(permission) ? "selected" : ""}><input type="checkbox" checked={value.includes(permission)} disabled={lockAdmin && permission === "admin"} onChange={() => toggle(permission)} /><span><Check /></span><strong>{permissionLabels[permission]}</strong></label>)}</fieldset>;
}

function UserAccessRow({ item, currentUserEmail, saving, deleting, onSave, onDelete }: { item: AppUser; currentUserEmail: string; saving: boolean; deleting: boolean; onSave: (payload: { email: string; name: string; permissions: AppPermission[] }) => void; onDelete: (email: string) => void }) {
  const effectivePermissions = allPermissions.filter((permission) => hasPermission(item, permission));
  const [permissions, setPermissions] = useState<AppPermission[]>(effectivePermissions);
  useEffect(() => setPermissions(effectivePermissions), [item.permissions, item.role]);
  const changed = allPermissions.some((permission) => permissions.includes(permission) !== effectivePermissions.includes(permission));
  const isCurrentUser = item.email === currentUserEmail;
  return <article className="access-user-row">
    <div className="access-user-row__identity"><span>{item.name.slice(0, 1).toUpperCase()}</span><div><strong>{item.name}</strong><small>{item.email}{isCurrentUser ? " · Sinä" : ""}</small></div></div>
    <PermissionSelector value={permissions} onChange={setPermissions} lockAdmin={isCurrentUser} />
    <div className="access-user-row__actions">
      <button className="button button--secondary" type="button" disabled={!changed || !permissions.length || saving} onClick={() => onSave({ email: item.email, name: item.name, permissions })}>{saving ? <LoaderCircle className="spin" /> : <Save />}Tallenna</button>
      <button className="icon-button icon-button--danger" type="button" aria-label={`Poista käyttäjä ${item.email}`} title="Poista käyttäjä" disabled={isCurrentUser || deleting} onClick={() => onDelete(item.email)}><Trash2 /></button>
    </div>
  </article>;
}
